<template>
    <div class="layout-px-spacing" v-if="hasViewPermission">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page">
                                    <span>Add Violation</span>
                                </li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <div class="row layout-top-spacing">
            <div class="col-xl-10 col-lg-10 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <router-link to="/violation/view" v-if="hasViewPermission">
                            <button type="button" class="btn btn-danger mb-2 me-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" class="feather feather-arrow-left">
                                    <line x1="19" y1="12" x2="5" y2="12"></line>
                                    <polyline points="12 19 5 12 12 5"></polyline>
                                </svg>
                                Back to Violations List
                            </button>
                        </router-link>

                        <h6 v-if="hasCreatePermission">Add your Violation Details here...</h6>

                        <!-- Form -->
                        <form @submit.prevent="submitForm" v-if="hasCreatePermission">
                            <div class="column">
                                <div class="col-md-12">
                                    <div class="mb-4">
                                        <label class="col-form-label" for="title">Violation Name</label>
                                        <input v-model="violation.title" type="text" id="title" class="form-control"
                                            placeholder="Required" required />
                                    </div>
                                    <div class="mb-4">
                                        <label class="col-form-label" for="description">Description</label>
                                        <input v-model="violation.description" type="text" id="description"
                                            class="form-control" placeholder="Required" required />
                                    </div>
                                </div>
                            </div>

                            <div class="text-center mt-3">
                                <div v-if="loading">
                                    <button type="button" class="btn btn-success btn-lg mb-3 me-3">
                                        <span
                                            class="spinner-grow text-white me-2 align-self-center loader-sm">Loading...</span>
                                        Processing
                                    </button>
                                </div>
                                <div v-else>
                                    <button type="submit" class="btn btn-success btn-lg mb-3 me-3">Submit</button>
                                </div>
                            </div>
                        </form>

                        <div v-else class="alert alert-danger">
                            You don't have permission to create violations.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div v-else class="layout-px-spacing">
        <div class="alert alert-danger">
            You don't have permission to view this page.
        </div>
    </div>
</template>

<style scoped>
.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}
</style>

<script setup>
import { useMeta } from "/src/composables/use-meta";
import { ref, computed } from 'vue';
import axios from 'axios';
import { BASE_URL } from '/src/Config/config';
import Swal from 'sweetalert2';
import { useStore } from 'vuex';

useMeta({ title: "Add Violation" });

const violation = ref({
    title: '',
    description: ''
});

const violationsList = ref([]);
const loading = ref(false);
const store = useStore();

const showSuccessAlert = (message) => {
    Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        padding: '2em',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false
    });
};

const showErrorAlert = (message) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
    });
};

const hasCreatePermission = computed(() => {
    return store.getters.hasPermission('create_violation');
});

const hasViewPermission = computed(() => {
    return store.getters.hasPermission('view_violation');
});

const submitForm = async () => {
    try {
        // Check permission before submitting
        if (!hasCreatePermission.value) {
            showErrorAlert("You don't have permission to create violations");
            return;
        }

        loading.value = true;

        const response = await axios.post(
            `${BASE_URL}/violations`,
            violation.value, // Request data
            { // Config object
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                }
            }
        );
        violationsList.value.push(response.data);

        // Reset the form fields
        violation.value = {
            title: '',
            description: ''
        };

        showSuccessAlert('Violation added successfully!');
    } catch (error) {
        console.error('Error adding violation:', error);
        let errorMessage = 'Failed to add violation!';

        if (error.response) {
            // Server responded with a status code outside 2xx
            errorMessage = error.response.data.message || errorMessage;
        } else if (error.request) {
            // Request was made but no response received
            errorMessage = 'Network error - please check your connection';
        }

        showErrorAlert(errorMessage);
    } finally {
        loading.value = false;
    }
};
</script>